// A&R und Katalog: Mix bouncen, anhören, als WAV abgeben; Projekte auf dem Server.
import { useEffect, useState } from 'react';
import { useStudio } from '../state/store.ts';
import { engine } from '../audio/engine.ts';
import { encodeWav } from '../audio/recorder.ts';
import { api } from '../api/client.ts';
import { Panel, toast } from '../components/ui.tsx';

/** Integrierte Lautheit (vereinfacht: K-Gewichtung angenähert, ohne Gating) – Richtwert für die Listening Session */
function loudness(buf: AudioBuffer) {
  let sum = 0, pk = 0;
  for (let c = 0; c < buf.numberOfChannels; c++) { const d = buf.getChannelData(c); for (let i = 0; i < d.length; i++) { sum += d[i] * d[i]; pk = Math.max(pk, Math.abs(d[i])); } }
  const ms = sum / (buf.length * buf.numberOfChannels);
  return { lufs: -0.691 + 10 * Math.log10(ms || 1e-12), peak: 20 * Math.log10(pk || 1e-12) };
}

export function Ar() {
  const project = useStudio((s) => s.project);
  const [res, setRes] = useState<{ url: string; lufs: number; peak: number; dur: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');
  const bounce = async () => {
    setBusy(true);
    try {
      const len = Math.max(...project.clips.map((c) => c.start + c.duration), 4) + 1;
      const buf = await engine.bounce(project, len);
      const l = loudness(buf);
      setRes({ url: URL.createObjectURL(encodeWav(buf)), lufs: l.lufs, peak: l.peak, dur: len });
    } finally { setBusy(false); }
  };
  return (
    <div className="grid-side">
      <Panel title="Listening Session" right={<button className="btn small primary" disabled={busy} onClick={() => void bounce()}>{busy ? 'Bounce …' : 'Mix bouncen'}</button>}>
        <div className="body stack">
          {res ? (
            <>
              <audio controls src={res.url} style={{ width: '100%' }} />
              <div className="row"><span className="pill">{res.dur.toFixed(1)} s</span><span className="pill blue">≈ {res.lufs.toFixed(1)} LUFS</span><span className={`pill ${res.peak > -1 ? 'red' : 'green'}`}>Spitze {res.peak.toFixed(1)} dBFS</span>
                <a className="btn small" href={res.url} download={`${project.name.replace(/\W+/g, '_')}_mix.wav`}>WAV 24 Bit</a></div>
              <div className="t2" style={{ fontSize: 12 }}>Hinweis: Der Bounce enthält die eingebauten Skizzen-Instrumente. Mit der Bridge werden eure Plugins vorher auf dem Studio-Rechner gerendert.</div>
            </>
          ) : <div className="empty">Noch kein Bounce</div>}
          <div className="field"><label>Notizen fürs Label</label><textarea className="input" rows={6} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Brief, Referenzen, Pitch-Ideen …" /></div>
        </div>
      </Panel>
      <Panel title="Song">
        <div className="body stack">
          <div className="h1">{project.name}</div>
          <div className="row"><span className="pill">{project.bpm} BPM</span><span className="pill">{project.key}</span>{project.srsWorkId && <span className="pill blue">{project.srsWorkId}</span>}</div>
          <div className="t2">{project.tracks.length} Spuren · {project.takes.length} Takes · {project.chords.map((c) => c.symbol).join(' – ')}</div>
        </div>
      </Panel>
    </div>
  );
}

export function Katalog() {
  const token = useStudio((s) => s.token);
  const project = useStudio((s) => s.project);
  const [list, setList] = useState<{ id: string; name: string; updatedAt: string; bpm: number; key: string }[]>([]);
  const load = () => api.listProjects().then(setList).catch(() => setList([]));
  useEffect(() => { if (token) void load(); }, [token]);
  const save = async () => { try { await api.saveProject(project); toast('Projekt gespeichert'); void load(); } catch (e) { toast((e as Error).message); } };
  const open = async (id: string) => { try { useStudio.getState().replaceProject(await api.getProject(id), false); useStudio.getState().setView('session'); } catch (e) { toast((e as Error).message); } };
  return (
    <Panel title="Projekte" style={{ height: '100%' }} right={<button className="btn small primary" disabled={!token} onClick={() => void save()}>Aktuelles Projekt speichern</button>}>
      <div className="body">
        {!token ? <div className="empty">Im Reiter Camp anmelden, um Projekte zu laden.</div> : !list.length ? <div className="empty">Noch keine Projekte auf dem Server.</div> : (
          <table className="table">
            <thead><tr><th>Titel</th><th>Tempo</th><th>Tonart</th><th>Geändert</th><th /></tr></thead>
            <tbody>{list.map((p) => <tr key={p.id}><td><b>{p.name}</b></td><td className="mono">{p.bpm}</td><td>{p.key}</td><td className="t2">{new Date(p.updatedAt).toLocaleString('de-DE')}</td><td><button className="btn small" onClick={() => void open(p.id)}>Öffnen</button></td></tr>)}</tbody>
          </table>
        )}
        <div className="card wash t2" style={{ marginTop: 14, fontSize: 12 }}>Hier wird der SRS-Katalog angebunden: Werke, Verträge, Opt-in fürs Training. Siehe Roadmap Phase 4.</div>
      </div>
    </Panel>
  );
}
